// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import type { GuidedProgressSection } from 'employment-insurance-data-access';
import type { GuidedTextResolver } from './guided-text';

/**
 * The six-section progress rail -- display only (sections are re-entered
 * from the review page's Change links, not from here). Each section shows
 * done, current or to do, with the BFF's one-line answer summary once it
 * is answered.
 */
export function ProgressRail({ progress, t }: { progress: GuidedProgressSection[]; t: GuidedTextResolver }) {
  const done = progress.filter((section) => section.status === 'done').length;
  return (
    <aside className="ei-guided__rail" aria-labelledby="ei-guided-rail-heading" data-testid="guided-progress">
      <h2 id="ei-guided-rail-heading" className="ei-guided__rail-heading">
        {t({ key: 'railHeading' })}
      </h2>
      <p className="ei-guided__rail-count">
        {done === progress.length ? t({ key: 'railComplete' }) : t({ key: 'railCount', params: { done: { t: 'number', v: done } } })}
      </p>
      <div className="ei-guided__rail-bar" aria-hidden="true">
        <span style={{ width: `${Math.round((done / progress.length) * 100)}%` }} />
      </div>
      <ol className="ei-guided__rail-list">
        {progress.map((section) => (
          <li
            key={section.section}
            className={`ei-guided__rail-item is-${section.status}`}
            data-section={section.section}
            data-status={section.status}
            aria-current={section.status === 'current' ? 'step' : undefined}
          >
            <span className="ei-guided__rail-node" aria-hidden="true">
              {section.status === 'done' ? '✓' : section.number}
            </span>
            <span className="ei-guided__rail-text">
              <span className="ei-guided__rail-label">{t({ key: `section.${section.section}` })}</span>
              {section.status === 'current' ? (
                <span className="ei-guided__rail-value">
                  {t({ key: 'railInProgress' })} <span className="ei-guided__rail-here">{t({ key: 'railHere' })}</span>
                </span>
              ) : section.summary ? (
                <span className="ei-guided__rail-value">{t(section.summary)}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
    </aside>
  );
}
